import { store } from '@/state';
import { togglePanel } from '@/ui/panels';
import { showToast } from '@/ui/toast';
import { updateStatsDisplay, setPauseIndicatorVisible } from '@/ui/stats';
import { addEventToFeed } from '@/ui/events';
import {
  togglePause,
  toggleRotation,
  setRotationSpeed,
  getRotationSpeed,
  zoomGlobe,
  resetGlobeView,
  takeScreenshot,
} from '@/visualization/globe';

/**
 * Initialize keyboard controls
 */
export function initKeyboardControls(): void {
  document.addEventListener('keydown', handleKeyDown);
}

/**
 * Remove keyboard controls
 */
export function removeKeyboardControls(): void {
  document.removeEventListener('keydown', handleKeyDown);
}

/**
 * Handle keydown events
 * @param event - Keyboard event
 */
function handleKeyDown(event: KeyboardEvent): void {
  // Ignore if typing in an input field
  if (
    event.target instanceof HTMLInputElement ||
    event.target instanceof HTMLTextAreaElement
  ) {
    return;
  }

  const key = event.key.toLowerCase();

  switch (key) {
    case 'l':
      togglePanel('left');
      break;

    case 'b':
      togglePanel('bottom');
      break;

    case 'p':
      handlePauseToggle();
      break;

    case ' ':
      event.preventDefault();
      handleRotationToggle();
      break;

    case 'c':
      handleClearFlows();
      break;

    case 's':
      if (!event.ctrlKey && !event.metaKey) {
        handleScreenshot();
      }
      break;

    case '+':
    case '=':
      handleZoomIn();
      break;

    case '-':
    case '_':
      handleZoomOut();
      break;

    case 'home':
      handleResetView();
      break;

    case 'f':
      handleSpeedUp();
      break;

    case 'j':
      handleSpeedDown();
      break;

    case 'h':
      handleShowHelp();
      break;
  }
}

/**
 * Handle pause toggle
 */
function handlePauseToggle(): void {
  const isPaused = togglePause();
  setPauseIndicatorVisible(isPaused);
  showToast(isPaused ? 'Paused' : 'Resumed');
}

/**
 * Handle rotation toggle
 */
function handleRotationToggle(): void {
  const isRotating = toggleRotation();
  showToast(isRotating ? 'Rotation ON' : 'Rotation OFF');
}

/**
 * Handle clear flows
 */
function handleClearFlows(): void {
  store.clearFlows();
  updateStatsDisplay();
  showToast('Flows cleared');
  addEventToFeed({
    severity: 'info',
    source: 'Operator',
    message: 'All flows cleared',
  });
}

/**
 * Handle screenshot
 */
function handleScreenshot(): void {
  takeScreenshot();
  showToast('Screenshot saved');
}

/**
 * Handle zoom in
 */
function handleZoomIn(): void {
  zoomGlobe(0.3);
  showToast('Zoomed in');
}

/**
 * Handle zoom out
 */
function handleZoomOut(): void {
  zoomGlobe(-0.3);
  showToast('Zoomed out');
}

/**
 * Handle reset view
 */
function handleResetView(): void {
  resetGlobeView();
  showToast('View reset');
}

/**
 * Handle speed up
 */
function handleSpeedUp(): void {
  const currentSpeed = getRotationSpeed();
  const newSpeed = Math.min(2.0, currentSpeed + 0.2);
  setRotationSpeed(newSpeed);
  showToast(`Speed: ${newSpeed.toFixed(1)}x`);
}

/**
 * Handle speed down
 */
function handleSpeedDown(): void {
  const currentSpeed = getRotationSpeed();
  const newSpeed = Math.max(0.1, currentSpeed - 0.2);
  setRotationSpeed(newSpeed);
  showToast(`Speed: ${newSpeed.toFixed(1)}x`);
}

/**
 * Handle show help
 */
function handleShowHelp(): void {
  showToast('L: Panel | B: Feed | P: Pause | Space: Rotate | C: Clear | S: Screenshot | +/-: Zoom | F/J: Speed');
}
